'use client'

import Link from 'next/link'
import s from './ArchiveComponent.module.scss'
import { useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import FeaturedProduct from '../../Singles/FeaturedProduct'
import LazyImage from '@/components/Common/LazyImage'
import { PortableText } from 'next-sanity'
import { portableBlockComponents } from '@/utils/portableText'
import { set } from 'sanity'
import SelectedWorkBlock from '@/components/Common/SelectedWorkBlock'

type Work = {
    slug: string
    title: string
    client?: { title?: string }
    author?: string
    categories?: Array<{ title?: string }>
    year?: number | string
    featuredImage?: { imageUrl: string; filename?: string; alt?: string; metadata?: { dimensions?: { width?: number; height?: number } } }
    info?: any[]
    projectLink?: string
    projectLinkText?: string
    pinterestLink?: string
    inWorkList?: boolean
    featuredImageList?: any[]
    awards?: Array<{ year: string; name: string }>
    credits?: Array<{ role: string; name: string }>
}

type SelectedBlock = { title?: string; contentLeft?: any[]; contentRight?: any[] } | null
type Props = { data: Work[]; selectedBlock?: SelectedBlock }

type SortKey = 'name' | 'client' | 'author' | 'categories' | 'year'
type SortOrder = 'asc' | 'desc'

const TOP_OFFSET = 113

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent)

function isScrollable(el: HTMLElement | null) {
  if (!el) return false
  // debe tener overflowY scroll/auto/overlay y contenido mayor que el viewport del contenedor
  const oy = getComputedStyle(el).overflowY
  const canScroll = oy === 'auto' || oy === 'scroll' || oy === 'overlay'
  return canScroll && el.scrollHeight > el.clientHeight + 1
}


function scrollToRow(el: HTMLElement) {
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export default function ArchiveComponent({ data, selectedBlock }: Props) {
    const [activeImage, setActiveImage] = useState<any>(data?.[0]?.featuredImage || null)
    const [sort, setSort] = useState<{ key: SortKey | null; order: SortOrder }>({ key: null, order: 'asc' })
    const [expandedSlug, setExpandedSlug] = useState<string | null>(null)
    const [lockActiveUntilMouseMove, setLockActiveUntilMouseMove] = useState(false)

    const scrollContainerRef = useRef<HTMLElement | null>(null)

    // refs por fila (li) usando el slug como clave
    const rowRefs = useRef<Record<string, HTMLLIElement | null>>({})

    // cuando cambia el expandedSlug, hacemos scroll

    useEffect(() => {
        if (!lockActiveUntilMouseMove) return

        const handleMove = () => {
            setLockActiveUntilMouseMove(false)
        }

        // escuchamos SOLO el siguiente movimiento
        window.addEventListener('mousemove', handleMove, { once: true })

        return () => {
            window.removeEventListener('mousemove', handleMove)
        }
    }, [lockActiveUntilMouseMove])

    const handleSort = (key: SortKey) =>
        setSort(p => (p.key === key ? { key, order: p.order === 'asc' ? 'desc' : 'asc' } : { key, order: 'asc' }))

    const getValue = (item: Work, key: SortKey) => {
        switch (key) {
            case 'name': return (item.title || '').toLowerCase()
            case 'client': return (item.client?.title || '').toLowerCase()
            case 'author': return (item.author || '').toLowerCase()
            case 'categories': return (item.categories?.map(c => c?.title || '').join(', ') || '').toLowerCase()
            case 'year': {
                const n = Number(item.year)
                return Number.isFinite(n) ? n : -Infinity
            }
        }
    }

    const sortedData = useMemo(() => {
        if (!sort.key) return data
        const arr = [...data]
        arr.sort((a, b) => {
            const av = getValue(a, sort.key!), bv = getValue(b, sort.key!)
            const comp = typeof av === 'number' && typeof bv === 'number' ? av - bv : (av < bv ? -1 : av > bv ? 1 : 0)
            return sort.order === 'asc' ? comp : -comp
        })
        return arr
    }, [data, sort])

    const arrowClass = (key: SortKey) =>
        `${s.arrow} ${sort.key === key ? (sort.order === 'asc' ? s.asc : s.desc) : ''}`

    // Helper para llevar la fila a Y = 113px en viewport
    const renderArrow = (key: SortKey) => {
        if (sort.key !== key) return '↓'        // columna sin ordenar o no activa
        return sort.order === 'asc' ? '↑' : '↓' // activa: asc = ↑, desc = ↓
    }

    // Toggle + scroll
    const toggleRow = (slug: string) => {
        const row = rowRefs.current[slug]
        if (!row) return

        // cerrar
        if (expandedSlug === slug) {
            setExpandedSlug(null)
            return
        }

        // posición absoluta ANTES (para detectar el salto provocado por colapsar el anterior)
        const beforeAbsTop = window.scrollY + row.getBoundingClientRect().top

        // abrimos el nuevo (esto colapsa el anterior si estaba abierto)
        setExpandedSlug(slug)

        // compensamos el salto en el siguiente frame
        requestAnimationFrame(() => {
            const afterAbsTop = window.scrollY + row.getBoundingClientRect().top
            const delta = afterAbsTop - beforeAbsTop
            if (Math.abs(delta) > 0.5) {
            window.scrollBy({ top: delta, behavior: 'auto' })
            }
        })
        }

    return (
        <>
        <div className={s.productsWork}>
            <div className={s.activeWorkImage}>
                <div className={s.imageLabel}>
                    {activeImage && (
                        <LazyImage
                            src={activeImage.imageUrl || activeImage}
                            alt={activeImage.filename || ''}
                            width={activeImage.metadata?.dimensions?.width || 800}
                            height={activeImage.metadata?.dimensions?.height || 600}
                        />
                    )}
                </div>
            </div>

            <div className={s.listContainer} ref={scrollContainerRef as any}>
                {sortedData?.length > 0 && (
                    <ul className={s.listProducts}>
                        <li className={s.heading}>
                            <button type="button" className={s.name} onClick={() => {handleSort('name'); setExpandedSlug(null);}}>
                                Name <span className={arrowClass('name')}>{renderArrow('name')}</span>
                            </button>
                            <button type="button" className={s.client} onClick={() => {handleSort('client'); setExpandedSlug(null);}}>
                                Client <span className={arrowClass('client')}>{renderArrow('client')}</span>
                            </button>
                            <button type="button" className={s.author} onClick={() => {handleSort('author'); setExpandedSlug(null);}}>
                                Author <span className={arrowClass('author')}>{renderArrow('author')}</span>
                            </button>
                            <button type="button" className={s.categories} onClick={() => {handleSort('categories'); setExpandedSlug(null);}}>
                                Categories <span className={arrowClass('categories')}>{renderArrow('categories')}</span>
                            </button>
                            <button type="button" className={s.year} onClick={() => {handleSort('year'); setExpandedSlug(null);}}>
                                Year <span className={arrowClass('year')}>{renderArrow('year')}</span>
                            </button>
                        </li>

                        {sortedData.map((rel) => {
                            const isOpen = expandedSlug === rel.slug;
                            const isDimmed = expandedSlug !== null && expandedSlug !== rel.slug

                            return (
                                <li
                                    key={rel.slug}
                                    ref={el => { rowRefs.current[rel.slug] = el }}
                                    className={`${s.row} ${isOpen ? s.open : ''} ${isDimmed ? s.dimmed : ''}`}
                                >
                                    {rel.inWorkList ? (
                                        <a href={`/work/${rel.slug}`} className="sr-only" tabIndex={-1}>{rel.title}</a>
                                    ) : rel.pinterestLink ? (
                                        <a href={rel.pinterestLink} className="sr-only" tabIndex={-1} rel="noopener noreferrer nofollow">{rel.title}</a>
                                    ) : rel.projectLink ? (
                                        <a href={rel.projectLink} className="sr-only" tabIndex={-1} rel="noopener noreferrer">{rel.title}</a>
                                    ) : null}
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        className={s.card}
                                        onMouseEnter={() => {
                                            if (lockActiveUntilMouseMove) return // 👈 mientras está bloqueado, ignoramos hovers
                                            setActiveImage(rel.featuredImage)
                                        }}
                                        onClick={(e) => {
                                            if (e.metaKey || e.ctrlKey) return // nueva pestaña
                                            e.preventDefault()
                                            
                                            // fijamos la imagen activa en la que acabamos de clicar
                                            if (rel.featuredImage) {
                                                setActiveImage(rel.featuredImage)
                                            }
                                            // bloqueamos los cambios por hover hasta que el mouse se mueva
                                            setLockActiveUntilMouseMove(true)

                                            toggleRow(rel.slug)
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                toggleRow(rel.slug)
                                            }
                                        }}
                                    >
                                        <h3>{rel.title}</h3>
                                        <div className={s.client}><p>{rel.client?.title}</p></div>
                                        <div className={s.author}><p>{rel.author}</p></div>
                                        <div className={s.categories}>
                                            {(rel.categories || []).map((cat, idx) => (
                                                <span key={idx}>
                                                {cat?.title}
                                                {idx < (rel.categories?.length || 0) - 1 && <br />}
                                                </span>
                                            ))}
                                        </div>
                                        <div className={s.year}><p>{rel.year}</p></div>
                                    </div>

                                    {/* Panel expandido */}
                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                key="expanded"
                                                className={s.expanded}
                                                initial={{ height: 0 }}
                                                animate={{ height: 'auto'}}
                                                exit={{ height: 0 }}
                                                transition={{ duration: 0.35, ease: 'easeInOut' }}
                                                onAnimationComplete={() => {
                                                    const row = rowRefs.current[rel.slug]
                                                    if (!row) return
                                                    requestAnimationFrame(() => {
                                                        row.scrollIntoView({ behavior: 'smooth', block: 'start' })
                                                    })
                                                }}
                                            >
                                                <div className={s.expandedInner}>
                                                    <div className={s.categoryAuthor}>
                                                            <p translate='no'>{rel.author}</p>
                                                            <p className={s.categoriesExpanded}>
                                                                {(rel.categories || []).map(cat => (
                                                                    <div key={`${cat?.title}`}>{cat?.title}</div>
                                                                ))}
                                                            </p>
                                                    </div>
                                                    {rel.credits && rel.credits.length > 0 && <div className={s.credits}>
                                                        {rel.credits && rel.credits.map((credit, index) => (
                                                            <div key={index} className={s.creditItem}>
                                                                <p className={s.creditRole}>{credit.role}</p>
                                                                <p translate='no' className={s.creditName}>{credit.name}</p>
                                                            </div>
                                                        ))}
                                                    </div>}
                                                    <div className={s.info}>
                                                        <h4>Info</h4>
                                                        <div className={s.infoContent}>
                                                            {rel?.info && rel?.info.map((textRow: any, index: number) => {
                                                                return (
                                                                    <PortableText
                                                                        key={textRow._key}
                                                                        value={[textRow]}
                                                                        components={portableBlockComponents()}
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    </div>

                                                    {rel?.awards && rel?.awards.length > 0 && <div className={s.awards}>
                                                        <h4>Awards</h4>
                                                        <div className={s.infoContent}>
                                                            {rel?.awards && rel?.awards.map((award: any, index: number) => {
                                                                return (
                                                                    <div key={index} className={s.awardItem}>
                                                                        <p className={s.awardYear}>{award.year}</p>
                                                                        <p translate='no' className={s.awardName}>{award.name}</p>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>}
                                                    {/* CTA opcional para navegar al detalle */}
                                                    {rel.inWorkList ? <div className={s.rowActions}>
                                                        <Link href={`/work/${rel.slug}`} className={s.viewLink}>
                                                            ↗ VIEW PROJECT
                                                        </Link>
                                                    </div> : rel.pinterestLink ? <div className={s.rowActions}>
                                                        <Link href={rel.pinterestLink} className={s.viewLink} target={"_blank"} rel="noopener noreferrer nofollow">
                                                            ↗ VIEW ON PINTEREST
                                                        </Link>
                                                    </div> : rel.projectLink && <div className={s.rowActions}>
                                                        <Link href={rel.projectLink} className={s.viewLink} target={"_blank"} rel="noopener noreferrer">
                                                            ↗ {rel.projectLinkText || "LINK TO WEBSITE"}
                                                        </Link>
                                                    </div>}

                                                    <div className={s.featuredImagesList}>
                                                        {rel.featuredImageList && rel.featuredImageList.map((img: any, idx: number) => (
                                                            <div key={idx} className={s.featuredImageItem} style={{aspectRatio: (img.metadata?.dimensions?.width && img.metadata?.dimensions?.height) ? `${img.metadata.dimensions.width} / ${img.metadata.dimensions.height}` : '4 / 3' }}>
                                                                <LazyImage
                                                                    src={img.imageUrl}
                                                                    alt={img.filename || ''}
                                                                    width={img.metadata?.dimensions?.width || 400}
                                                                    height={img.metadata?.dimensions?.height || 300}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
        </div>
        <SelectedWorkBlock data={selectedBlock} />
        </>
    )
}